import React, { useEffect, useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Typography,
  TextField,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { UserCategoryViewModel } from "../../../../configuration/api/apiSchema";

interface CollapsibleCategoriesListProps {
  categories: UserCategoryViewModel[];
  onRateChange?: (updatedCategory: UserCategoryViewModel) => void;
  userId?: string;
}

const CollapsibleCategoriesList: React.FC<CollapsibleCategoriesListProps> = ({
  categories,
  onRateChange,
  userId,
}) => {
  const isReadonly = userId !== undefined;

  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({});
  const [rates, setRates] = useState<Record<string, { hourlyRate: string; fixedPriceRate: string }>>({});

  useEffect(() => {
    const newRates: Record<string, { hourlyRate: string; fixedPriceRate: string }> = {};
    const traverse = (cats: UserCategoryViewModel[]) => {
      cats.forEach((cat) => {
        const key = cat.key || "";
        newRates[key] = {
          hourlyRate: cat.hourlyRate?.toString() || "",
          fixedPriceRate: cat.fixedPriceRate?.toString() || "",
        };
        if (cat.subcategories) {
          traverse(cat.subcategories);
        }
      });
    };
    traverse(categories);
    setRates(newRates);
  }, [categories]);

  const handleToggle = (key: string) => {
    setOpenCategories((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleLocalRateChange = (
    category: UserCategoryViewModel,
    field: "hourlyRate" | "fixedPriceRate",
    newValue: string
  ) => {
    setRates((prev) => ({
      ...prev,
      [category.key!]: {
        ...prev[category.key!],
        [field]: newValue,
      },
    }));
  };

  const commitRateChange = (
    category: UserCategoryViewModel,
    field: "hourlyRate" | "fixedPriceRate"
  ) => {
    if (!onRateChange || !category.key) return;
    const rawValue = rates[category.key][field];
    const updatedValue = parseFloat(rawValue) || 0;

    const updatedCategory: UserCategoryViewModel = {
      ...category,
      [field]: updatedValue,
    };

    onRateChange(updatedCategory);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    category: UserCategoryViewModel,
    field: "hourlyRate" | "fixedPriceRate"
  ) => {
    if (event.key === "Enter") {
      commitRateChange(category, field);
      (event.target as HTMLInputElement).blur();
    }
  };

  const renderCategory = (category: UserCategoryViewModel, level: number = 0) => {
    const categoryKey = category.key || "";
    const isOpen = !!openCategories[categoryKey];
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;

    return (
      <Box key={categoryKey} sx={{ ml: level * 2, mb: 2 }}>
        <ListItemButton
          onClick={() => handleToggle(categoryKey)}
          sx={{
            backgroundColor: isOpen ? "#f9f9f9" : "transparent",
            borderRadius: 2,
            mb: 1,
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#e0e0e0",
            },
          }}
        >
          <ListItemText primary={category.name} />
          {hasSubcategories && (isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
        </ListItemButton>

        <Box sx={{ ml: 3, mb: 2 }}>
          <TextField
            label="Hourly Rate"
            type="number"
            value={rates[categoryKey]?.hourlyRate ?? ""}
            onChange={(e) => handleLocalRateChange(category, "hourlyRate", e.target.value)}
            onBlur={() => commitRateChange(category, "hourlyRate")}
            onKeyDown={(e) => handleKeyDown(e as any, category, "hourlyRate")}
            sx={{ width: { xs: "100%", sm: 200 }, mb: 1 }}
            disabled={isReadonly}
          />
          <TextField
            label="Fixed Price Rate"
            type="number"
            value={rates[categoryKey]?.fixedPriceRate ?? ""}
            onChange={(e) => handleLocalRateChange(category, "fixedPriceRate", e.target.value)}
            onBlur={() => commitRateChange(category, "fixedPriceRate")}
            onKeyDown={(e) => handleKeyDown(e as any, category, "fixedPriceRate")}
            sx={{ width: { xs: "100%", sm: 200 } }}
            disabled={isReadonly}
          />
        </Box>

        {hasSubcategories && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {category.subcategories!.map((sub) => renderCategory(sub, level + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Categories
      </Typography>
      <List>
        {categories.map((category) => renderCategory(category))}
      </List>
    </Box>
  );
};

export default CollapsibleCategoriesList;
